import { createHash } from "node:crypto";
import {
  chmodSync,
  existsSync,
  lstatSync,
  mkdirSync,
  readFileSync,
  readdirSync,
  realpathSync,
  renameSync,
  rmSync,
  writeFileSync,
} from "node:fs";
import { isAbsolute, join, relative } from "node:path";
import { openSqlite } from "../../../core/src/logging/sinks/sqlite-runtime.js";
import type { CodexCredentialDelivery } from "@skaile/workspaces/types";
import type { AgentConfig, CodexManagedRuntimeOptions } from "../types.js";

import { CODEX_NATIVE_STATE_FORMAT, CodexNativeStateUnavailable } from "./native-state.js";

const invalid = () => new Error("AI_PROFILE_INVALID");
const within = (path: string, root: string) => {
  const rel = relative(root, path);
  return rel === "" || (!rel.startsWith("..") && !isAbsolute(rel));
};
function directory(path: string): void {
  if (!existsSync(path)) mkdirSync(path, { mode: 0o700 });
  const stat = lstatSync(path);
  if (!stat.isDirectory() || stat.isSymbolicLink() || stat.mode & 0o077) throw invalid();
}
function readRegular(path: string, limit: number): Buffer {
  const stat = lstatSync(path);
  if (!stat.isFile() || stat.isSymbolicLink() || stat.size > limit) throw invalid();
  return readFileSync(path);
}

/** Session-owned state lock and account-bound native resume metadata; never stores credentials. */
export function prepareCodexInstance(
  config: AgentConfig,
  options: CodexManagedRuntimeOptions,
  credential: CodexCredentialDelivery,
) {
  if (!isAbsolute(options.privateDirectory) || !isAbsolute(config.cwd) || !config.sessionId)
    throw invalid();
  directory(options.privateDirectory);
  const root = realpathSync(options.privateDirectory);
  const cwd = realpathSync(config.cwd);
  if (within(root, cwd) || within(cwd, root)) throw invalid();
  const lock = join(root, ".active");
  const leasePath = join(root, "lease.db");
  if (existsSync(leasePath) && !lstatSync(leasePath).isFile()) throw invalid();
  const lease = openSqlite(leasePath);
  try {
    lease.exec("PRAGMA busy_timeout=0; BEGIN EXCLUSIVE");
    chmodSync(leasePath, 0o600);
    // The OS releases the database lease on crashes; this marker never owns the lock.
    rmSync(lock, { force: true });
    writeFileSync(lock, "active", { flag: "wx", mode: 0o600 });
  } catch (error) {
    lease.close();
    throw error;
  }
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    try {
      rmSync(lock, { force: true });
    } finally {
      lease.close();
    }
  };
  try {
    const key = createHash("sha256")
      .update(
        JSON.stringify({
          driver: "codex-app-server-v1",
          session: config.sessionId,
          config: credential.configId,
          kind: credential.kind,
          account: credential.kind === "codex-subscription" ? credential.chatgptAccountId : null,
        }),
      )
      .digest("hex");
    const slots = join(root, CODEX_NATIVE_STATE_FORMAT);
    directory(slots);
    const stateRoot = join(slots, key);
    directory(stateRoot);
    for (const name of ["instructions.new", "binding.new"])
      rmSync(join(stateRoot, name), { force: true });
    const bindingPath = join(stateRoot, "binding.json");
    if (config.resumeSessionId && !existsSync(bindingPath)) throw new CodexNativeStateUnavailable();
    if (existsSync(bindingPath)) {
      // Filesystem authority failures (including symlinks) are fatal, not missing state.
      const bytes = readRegular(bindingPath, 4096);
      if (config.resumeSessionId) {
        let binding: unknown;
        try {
          binding = JSON.parse(bytes.toString("utf8"));
        } catch {
          throw new CodexNativeStateUnavailable();
        }
        if (
          !binding ||
          typeof binding !== "object" ||
          Array.isArray(binding) ||
          Object.keys(binding).sort().join(",") !== "format,key,threadId" ||
          (binding as Record<string, unknown>).format !== CODEX_NATIVE_STATE_FORMAT ||
          (binding as Record<string, unknown>).key !== key ||
          (binding as Record<string, unknown>).threadId !== config.resumeSessionId
        )
          throw new CodexNativeStateUnavailable();
      }
    }
    const home = join(stateRoot, "home");
    if (config.resumeSessionId && !existsSync(home)) throw new CodexNativeStateUnavailable();
    directory(home);
    for (const subdir of ["config", "cache", "data", "tmp"]) directory(join(home, subdir));
    // Auth persistence is forbidden even if a previous external process left a carrier here.
    if (["auth.json", ".credentials.json"].some((name) => existsSync(join(home, name))))
      throw invalid();
    const instructionFile = join(stateRoot, "instructions.md");
    const instructions = [
      ...(options.instructionFiles ?? []).map((path) =>
        readRegular(path, 1024 * 1024).toString("utf8"),
      ),
      ...(config.systemPrompt ? [config.systemPrompt] : []),
    ].join("\n\n---\n\n");
    const temporary = join(stateRoot, "instructions.new");
    writeFileSync(temporary, instructions, { mode: 0o600, flag: "wx" });
    renameSync(temporary, instructionFile);
    const target = join(home, "skills");
    if (!options.skillsDirectory && existsSync(target)) rmSync(target, { recursive: true });
    if (options.skillsDirectory) {
      const source = realpathSync(options.skillsDirectory);
      if (within(target, source) || within(source, target)) throw invalid();
      if (existsSync(target)) rmSync(target, { recursive: true });
      let files = 0;
      let entries = 0;
      let bytes = 0;
      const copy = (source: string, destination: string) => {
        if (++entries > 1024) throw invalid();
        if (lstatSync(source).isSymbolicLink()) throw invalid();
        mkdirSync(destination, { mode: 0o700 });
        for (const entry of readdirSync(source, { withFileTypes: true })) {
          const src = join(source, entry.name);
          const dst = join(destination, entry.name);
          if (entry.isSymbolicLink()) throw invalid();
          if (entry.isDirectory()) copy(src, dst);
          else {
            const content = readRegular(src, 1024 * 1024);
            bytes += content.byteLength;
            if (++files > 512 || bytes > 8 * 1024 * 1024) throw invalid();
            writeFileSync(dst, content, { mode: 0o600, flag: "wx" });
          }
        }
      };
      copy(options.skillsDirectory, target);
    }
    const bindThread = (threadId: string) => {
      if (!threadId || threadId.length > 4096) throw invalid();
      const next = join(stateRoot, "binding.new");
      writeFileSync(next, JSON.stringify({ format: CODEX_NATIVE_STATE_FORMAT, key, threadId }), {
        mode: 0o600,
        flag: "wx",
      });
      renameSync(next, bindingPath);
    };
    const setToolInstructions = (text: string) => {
      writeFileSync(temporary, `${instructions}\n\n${text}`, { mode: 0o600, flag: "wx" });
      renameSync(temporary, instructionFile);
    };
    return {
      root,
      stateRoot,
      cwd,
      home,
      instructionFile,
      bindThread,
      release,
      setToolInstructions,
    };
  } catch (error) {
    release();
    throw error;
  }
}

/** A replacement environment; managed auth never reads or mutates ambient process.env. */
export function codexInstanceEnvironment(
  input: Record<string, string>,
  home: string,
): Record<string, string> {
  const allowed = new Set([
    "PATH",
    "LANG",
    "LC_ALL",
    "TZ",
    "SSL_CERT_FILE",
    "SSL_CERT_DIR",
    "HTTPS_PROXY",
    "HTTP_PROXY",
    "NO_PROXY",
  ]);
  if (
    !input ||
    Object.entries(input).some(([key, value]) => !allowed.has(key) || typeof value !== "string")
  )
    throw invalid();
  return {
    ...input,
    HOME: home,
    CODEX_HOME: home,
    XDG_CONFIG_HOME: join(home, "config"),
    XDG_CACHE_HOME: join(home, "cache"),
    XDG_DATA_HOME: join(home, "data"),
    TMPDIR: join(home, "tmp"),
  };
}
